/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 23 2023
 **/

import { useEffect, useState, memo, MouseEvent } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, Divider, Typography, FoldingB } from "@/frameworks/designStuff/components";
import { Drawer as D } from "@/frameworks/designStuff/compounds";
import { Maybe } from "@/frameworks/libraries/components";
import { POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { useMiniChatPopupManager, usePopover, useSidebarActions } from "@/frameworks/core/hooks";
import { useChatDataStore } from "@/modules/chat/store";

import * as S from "./emotion";
import { DirectChatList, SpaceChatList, ChatSearch, ChatUIOptionMenu } from "./components";

function ChatSidebar() {
	const [currentChatSelected, setCurrentChatSelected] = useState<string>("");
	const popover = usePopover();
	const sidebarActions = useSidebarActions();
	const { miniChatPopupActions, miniChat } = useMiniChatPopupManager();
	const { idFullSizeChat, prioritySpaceChat, totalChat_search_area_open, set_totalChat_search_area_open } =
		useChatDataStore(
			useShallow((state) => ({
				idFullSizeChat: state.fullsize_chat_id,
				prioritySpaceChat: state.priority_space_chat,
				set_totalChat_search_area_open: state.set_totalChat_search_area_open,
				totalChat_search_area_open: state.totalChat_search_area_open
			}))
		);

	const currentChatFocus = miniChatPopupActions.getCurrentFocus();
	const miniChatValue = miniChat(currentChatFocus?.id || "", currentChatFocus?.spaceID || "");
	const closeSearchAreaIfOpen = () => {
		if (totalChat_search_area_open) {
			set_totalChat_search_area_open(false);
		}
	};
	const handleSelectChat = (channel: string) => {
		setCurrentChatSelected(channel);
		closeSearchAreaIfOpen();
	};
	const handleClickClose = () => sidebarActions.hideAll("left");
	const handleClickMore = (ev: MouseEvent<HTMLButtonElement>) => {
		popover.toggle({
			popoverID: POPOVER_ID.CHAT_UI_OPTION,
			anchorEl: ev.currentTarget,
			content: <ChatUIOptionMenu />,
			position: {
				x: -60,
				y: 0
			},
			zIndex: ZIndex.SIDEBAR + 1
		});
	};

	useEffect(() => {
		handleSelectChat(miniChatValue?.focus && miniChatValue?.id ? miniChatValue.id : "");
	}, [miniChatValue?.id]);

	useEffect(() => {
		idFullSizeChat && handleSelectChat(idFullSizeChat);
	}, [idFullSizeChat]);

	return (
		<D.Wrap>
			<D.Header>
				<S.Header verticalAlign="center" horizonAlign="distribute">
					<Typography typoSize="Head6">Chat</Typography>
					<S.HeaderIconList>
						<Button.Icon
							name="Search24"
							size="large"
							variant="text"
							onClick={() => set_totalChat_search_area_open(!totalChat_search_area_open)}
						/>
						<Button.Icon name="More24" size="large" variant="text" onClick={handleClickMore} />
						<Button.Icon name="Close24" size="large" variant="text" onClick={handleClickClose} />
					</S.HeaderIconList>
				</S.Header>
				<Maybe test={totalChat_search_area_open}>
					<ChatSearch onClose={() => set_totalChat_search_area_open(false)} />
				</Maybe>
				<Divider type="line" />
			</D.Header>
			<D.Body css={[{ width: "100%" }]}>
				<FoldingB.Wrap>
					<Maybe test={prioritySpaceChat}>
						<SpaceChatList currentChatSelected={currentChatSelected} handleSelectChat={handleSelectChat} />
						<DirectChatList currentChatSelected={currentChatSelected} handleSelectChat={handleSelectChat} />
						<Maybe.Fallback>
							<DirectChatList
								currentChatSelected={currentChatSelected}
								handleSelectChat={handleSelectChat}
							/>
							<SpaceChatList
								currentChatSelected={currentChatSelected}
								handleSelectChat={handleSelectChat}
							/>
						</Maybe.Fallback>
					</Maybe>
				</FoldingB.Wrap>
			</D.Body>
		</D.Wrap>
	);
}

export default memo(ChatSidebar);
